import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes, FaTimesCircle, FaCheckCircle, FaExclamationTriangle, FaClock } from 'react-icons/fa';
import RenderSelections from '../../../../../Common/RenderSelections/RenderSelections';
import { markPedidoAsCancelledById } from '../../../../../../../../crud/pedidos_api/pedidos';
import { toast } from 'react-toastify';
import { Pedido } from '../../../../../types';


interface PedidoDetailProps {
  show: boolean;
  onClose: () => void;
  pedido: Pedido | null;
  pedidosTypeMap?: Record<number, string>;
  onPedidoCancelled?: (pedidoId: number) => void;
}

const PedidoDetail: React.FC<PedidoDetailProps> = ({ 
    show, 
    onClose, 
    pedido,
    pedidosTypeMap,
    onPedidoCancelled
 }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancelPedido = () => {
    if (!pedido) return;
    setCancelling(true);
    markPedidoAsCancelledById(pedido.pedido_id)
      .then(() => {
        toast.success('Pedido cancelado');
        setShowConfirm(false);
        onClose();
        onPedidoCancelled?.(pedido.pedido_id);
      })
      .catch((error) => {
        console.error('Error cancelando el pedido: ', error);
        toast.error('Error al cancelar el pedido');
      })
      .finally(() => setCancelling(false));
  };

  const getStatusBadge = () => {
    if (pedido?.cancelled) {
      return <span className="mp-badge mp-badge-danger"><FaTimesCircle className="mp-badge-icon" /> Cancelado</span>;
    } else if (pedido?.closed) {
      return <span className="mp-badge mp-badge-success"><FaCheckCircle className="mp-badge-icon" /> Pagado</span>;
    } else if (pedido?.printed && !pedido?.closed && !pedido?.cancelled && !pedido?.isDelivered && !pedido?.isReady) {
      return <span className="mp-badge mp-badge-warning"><FaExclamationTriangle className="mp-badge-icon" /> Impreso</span>;
    } else if (!pedido?.closed && !pedido?.cancelled && !pedido?.isDelivered && pedido?.isReady) {
      return <span className="mp-badge mp-badge-warning"><FaExclamationTriangle className="mp-badge-icon" /> Listo</span>;
    } else if (!pedido?.closed && !pedido?.cancelled && pedido?.isDelivered && pedido?.isReady) {
      return <span className="mp-badge mp-badge-warning"><FaExclamationTriangle className="mp-badge-icon" /> Entregado</span>;
    } else {
      return <span className="mp-badge mp-badge-info"><FaClock className="mp-badge-icon" /> Creado</span>;
    }
  };

  const formatTimestamp = () => {
    if (!pedido) return '';
    const timestamp = pedido.local_timestamp || pedido.timestamp;
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityBadge = (priority: string) => {
    const lower = priority.toLowerCase();
    let cls = 'mp-modal-priority-medium';
    if (lower === 'alta' || lower === 'high' || lower === 'urgente') cls = 'mp-modal-priority-high';
    else if (lower === 'baja' || lower === 'low') cls = 'mp-modal-priority-low';
    return <span className={`mp-modal-priority-badge ${cls}`}>{priority}</span>;
  };

  const pedidoTypeName = pedido?.pedido_type_id && pedidosTypeMap
    ? pedidosTypeMap[pedido.pedido_type_id]
    : null;

  const hasAnyAdditionalItem = Boolean(
    (pedido?.hora_entrega && !isNaN(new Date(pedido.hora_entrega).getTime())) ||
    pedido?.comment ||
    pedido?.third_party_provider_name ||
    pedido?.third_party_provider_code ||
    pedido?.priority ||
    pedido?.shift_name ||
    pedidoTypeName
  );

  const selectionCount = pedido?.selections?.length || 0;
  const canCancel = !pedido?.closed && !pedido?.isDelivered && !pedido?.cancelled;

    return (
      <Modal 
        show={show} 
        onHide={onClose} 
        onClick={(e:any) => e.stopPropagation()} 
        className="mp-modal" 
        size="lg" 
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="mp-modal-header">
          <Modal.Title className="mp-modal-title">Detalle del Pedido</Modal.Title>
          <button className="mp-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </Modal.Header>

        <Modal.Body className="mp-modal-body mp-scrollbar">
          {/* Identity header card */}
          <div className="mp-modal-identity-card">
            <div className="mp-modal-identity-row">
              <div className="mp-modal-identity-badges">
                {pedido?.user_name && (
                  <span className="mp-modal-id-badge mp-modal-id-badge-user">{pedido.user_name}</span>
                )}
                {pedido?.mesa_num && (
                  <span className="mp-modal-id-badge mp-modal-id-badge-orange">Mesa {pedido.mesa_num}</span>
                )}
                {pedido?.client_name && (
                  <span className="mp-modal-id-badge mp-modal-id-badge-neutral">{pedido.client_name}</span>
                )}
              </div>
              {getStatusBadge()}
            </div>
            <div className="mp-modal-identity-row" style={{ marginBottom: 0 }}>
              <span className="mp-modal-timestamp">
                {formatTimestamp()}
              </span>
              {pedido?.pedido_id && (
                <span className="mp-modal-pedido-id">#{pedido.pedido_id}</span>
              )}
            </div>
          </div>

          {/* Products Section */}
          <div className="mp-modal-divider" />
          <div className="mp-modal-section-header">
            <span className="mp-modal-section-title">Productos Ordenados</span>
            <span className="mp-modal-count-pill">
              {selectionCount} {selectionCount === 1 ? 'producto' : 'productos'}
            </span>
          </div>
          <div className="mp-modal-content-card">
            <RenderSelections selections={pedido?.selections as any ?? []} showTotal={true} />
          </div>

          {/* Additional Info Section */}
          {hasAnyAdditionalItem ? (
            <>
              <div className="mp-modal-divider" />
              <div className="mp-modal-section-header">
                <span className="mp-modal-section-title">Información Adicional</span>
              </div>
              <div className="mp-modal-info-stack">
                {pedidoTypeName && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Tipo:</span>
                    <span className="mp-modal-info-value">{pedidoTypeName}</span>
                  </div>
                )}
                {pedido?.shift_name && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Turno:</span>
                    <span className="mp-modal-info-value">{pedido.shift_name}</span>
                  </div>
                )}
                {pedido?.hora_entrega && !isNaN(new Date(pedido.hora_entrega).getTime()) && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Hora entrega:</span>
                    <span className="mp-modal-info-value">
                      {new Date(pedido.hora_entrega).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {pedido?.comment && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Comentario:</span>
                    <span className="mp-modal-info-value">{pedido.comment}</span>
                  </div>
                )}
                {pedido?.third_party_provider_name && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Proveedor:</span>
                    <span className="mp-modal-info-value">{pedido.third_party_provider_name}</span>
                  </div>
                )}
                {pedido?.third_party_provider_code && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Código:</span>
                    <span className="mp-modal-info-value">{pedido.third_party_provider_code}</span>
                  </div>
                )}
                {pedido?.priority && (
                  <div className="mp-modal-info-row">
                    <span className="mp-modal-info-label">Prioridad:</span>
                    <span className="mp-modal-info-value">{getPriorityBadge(pedido.priority)}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="mp-modal-empty-state">
              No existen comentarios adicionales
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="mp-modal-footer">
          <button className="mp-modal-btn-secondary" onClick={onClose}>
            Cerrar
          </button>
          {canCancel && (
            <button className="mp-modal-btn-danger" onClick={() => setShowConfirm(true)}>
              Cancelar Pedido
            </button>
          )}
        </Modal.Footer>

        {/* Confirmation overlay */}
        {showConfirm && (
          <div className="mp-modal-confirm-overlay" onClick={() => !cancelling && setShowConfirm(false)}>
            <div className="mp-modal-confirm-card" onClick={(e) => e.stopPropagation()}>
              <div className="mp-modal-confirm-title">¿Cancelar este pedido?</div>
              <div className="mp-modal-confirm-text">
                Esta acción no se puede deshacer. El pedido será marcado como cancelado.
              </div>
              <div className="mp-modal-confirm-actions">
                <button
                  className="mp-modal-btn-secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={cancelling}
                >
                  Volver
                </button>
                <button
                  className="mp-modal-btn-danger"
                  onClick={handleCancelPedido}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelando...' : 'Sí, cancelar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    )
};

export default PedidoDetail;
